import {
  ConversationResponse,
  MessageResponse,
  MessageType,
} from "@/generated-api";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { BanIcon, Trash2, UserIcon } from "lucide-react";
import sentIcon from "../../assets/sent-check.svg";
import readIcon from "../../assets/read-check.svg";
import { useEffect, useState } from "react";
import { useBlockUser, useDeleteConversation } from "@/tanstack-hooks";
import { Button } from "../ui/button";
import { useToast } from "@/hooks/use-toast";
import OnlineIndicator from "../profile/OnlineIndicator";
import pinGray from "../../assets/pin-gray.svg";
import { useTranslation } from "@/hooks/useTranslation";
import { TranslationTree } from "@/translations";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";

const formatMessageDate = (sentAt: string, locale: string = "en") => {
  const date = new Date(sentAt);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isThisYear = date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  if (diffDays < 7) {
    return date.toLocaleDateString(locale, { weekday: "short" });
  }

  if (isThisYear) {
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
    });
  }

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

function getMessagePreview(
  message: MessageResponse,
  counterpartyId: string,
  t: TranslationTree
): string {
  if (message.message_type === MessageType.TEXT) {
    return message.content;
  }
  if (message.message_type === MessageType.PHOTO_SHARE) {
    if (message.sender_id === counterpartyId) {
      return t.PHOTO_SHARING.LABELS.SHARED_MESSAGE(message.content);
    } else {
      return t.PHOTO_SHARING.LABELS.SHARED_MESSAGE();
    }
  }
  if (message.message_type === MessageType.PHOTO_REQUEST) {
    if (message.sender_id === counterpartyId) {
      return t.PHOTO_SHARING.LABELS.REQUESTED_MESSAGE(message.content);
    } else {
      return t.PHOTO_SHARING.LABELS.REQUESTED_MESSAGE();
    }
  }
  return message.content;
}

export default function ConversationRow({
  conversation,
  selectedConversationId,
  selectedCounterpartyId,
  onClick,
  onAvatarClick,
  onDelete,
  onSwipeStateChange,
  onInteraction,
}: {
  conversation: ConversationResponse;
  selectedConversationId: string | undefined;
  selectedCounterpartyId?: string;
  onClick: () => void;
  onAvatarClick: () => void;
  onDelete: (counterpartyId: string) => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
  onInteraction?: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const deleteConversationMutation = useDeleteConversation();
  const blockMutation = useBlockUser();
  const t = useTranslation();
  const locale = useGetAccountLocale();

  const BUTTON_WIDTH = 96; // w-24 = 6rem = 96px
  const TOTAL_BUTTONS_WIDTH = BUTTON_WIDTH * 2 + 5; // Two buttons and padding
  const SWIPE_THRESHOLD = TOTAL_BUTTONS_WIDTH / 2;

  const resetSwipeState = () => {
    setOffset(0);
    onSwipeStateChange?.(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    onInteraction?.();
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    const newOffset = Math.min(0, Math.max(-TOTAL_BUTTONS_WIDTH, diff));
    setOffset(newOffset);
  };

  const handleTouchEnd = () => {
    if (offset < -SWIPE_THRESHOLD) {
      setOffset(-TOTAL_BUTTONS_WIDTH);
      onSwipeStateChange?.(true);
    } else {
      resetSwipeState();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onInteraction?.();
    setOffset(-TOTAL_BUTTONS_WIDTH);
    onSwipeStateChange?.(true);
  };

  const handleBlockUser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(true);
    blockMutation.mutate(conversation.counterparty.counterparty_id, {
      onError: () => {
        setIsDeleting(false);
        toast({
          variant: "destructive",
          title: t.PROFILE_VIEW.ERRORS.BLOCK,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
    onDelete(conversation.counterparty.counterparty_id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(true);
    deleteConversationMutation.mutate(
      conversation.counterparty.counterparty_id,
      {
        onError: () => {
          setIsDeleting(false);
          toast({
            variant: "destructive",
            title: t.MESSAGES.ERRORS.DELETE_CONVERSATION,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      }
    );
    onDelete(conversation.counterparty.counterparty_id);
  };

  const handleClick = () => {
    resetSwipeState();
    onInteraction?.();
    onClick();
  };

  useEffect(() => {
    const closeSwipe = () => {
      resetSwipeState();
    };

    window.addEventListener("closeAllSwipes", closeSwipe);
    return () => window.removeEventListener("closeAllSwipes", closeSwipe);
  }, []);

  useEffect(() => {
    resetSwipeState();
    return () => resetSwipeState();
  }, []);

  const rowHighlighted = selectedConversationId
    ? selectedConversationId === conversation.conversation_id
    : selectedCounterpartyId === conversation.counterparty.counterparty_id;

  return (
    <div className="relative overflow-hidden">
      <div
        className={`absolute right-0 top-0 bottom-0 flex items-center justify-center ${
          isDeleting ? "animate-slide-left-button" : ""
        }`}
      >
        <Button
          onClick={handleBlockUser}
          className="flex flex-col gap-y-1 hover:bg-slate-800 bg-slate-900 items-center rounded-l-lg rounded-r-none justify-center w-24 h-full"
        >
          <BanIcon className="text-white" size={24} />
          <span className="text-xs text-white">
            {t.PROFILE_VIEW.LABELS.BLOCK_BUTTON}
          </span>
        </Button>
        <Button
          onClick={handleDelete}
          variant="destructive"
          className="flex flex-col gap-y-1 dark items-center rounded-l-none rounded-r-lg justify-center w-24 h-full"
        >
          <Trash2 className="text-white" size={24} />
          <span className="text-xs text-white">
            {t.MESSAGES.LABELS.DELETE_BUTTON}
          </span>
        </Button>
      </div>

      <div
        className={`relative transform transition-transform duration-200 ${
          isDeleting ? "animate-slide-left" : ""
        }`}
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
      >
        <div
          className={`flex items-start p-2 rounded-lg cursor-pointer transition-colors ${
            rowHighlighted ? "bg-zinc-800" : "bg-black"
          }`}
          onClick={handleClick}
        >
          <div className="relative">
            <Avatar
              className="h-16 w-16 mr-3"
              onClick={(e) => {
                e.stopPropagation();
                onAvatarClick();
              }}
            >
              <AvatarImage
                src={conversation.counterparty.counterparty_photo_url}
                className="object-cover scale-125"
              />
              <AvatarFallback>
                <UserIcon size={24} className="text-gray-400" />
              </AvatarFallback>
            </Avatar>
            <OnlineIndicator
              lastActiveAt={conversation.counterparty.last_active_at}
              className="absolute bottom-0 right-4 border-2 border-black h-4 w-4 md:w-4 md:h-4"
            />
          </div>
          <div className="flex flex-1 min-w-0 pb-[5px] border-b border-zinc-600">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate flex flex-row">
                {conversation.counterparty.counterparty_name},{" "}
                {conversation.counterparty.counterparty_age}
              </div>
              <div className="flex flex-col justify-start">
                <div className="flex flex-row text-gray-400 text-sm items-center">
                  <img src={pinGray} className="w-3 h-3 mr-1" />
                  {conversation.counterparty.counterparty_city.name}
                </div>
                <div className="flex flex-row items-center gap-2">
                  {conversation.last_message.sender_id !==
                    conversation.counterparty.counterparty_id &&
                    (conversation.last_message.read_at ? (
                      <img src={readIcon} className="h-4 w-4" />
                    ) : (
                      <img src={sentIcon} className="h-3 w-3" />
                    ))}
                  <p className="text-sm text-zinc-500 text-ellipsis truncate">
                    {getMessagePreview(
                      conversation.last_message,
                      conversation.counterparty.counterparty_id,
                      t
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="ml-3 flex flex-col gap-y-3 items-end">
              <div className="text-sm text-gray-400">
                {formatMessageDate(
                  conversation.last_message.sent_at,
                  locale.code
                )}
              </div>
              {conversation.unread_count > 0 && (
                <div
                  className={`flex items-center justify-center text-black h-5 ${
                    conversation.unread_count < 10 && "w-5"
                  } px-1 bg-white rounded-full font-medium text-xs`}
                >
                  {conversation.unread_count}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
